/* ==========================================================================
   TWFISHING — theme.js
   Philosophy: the site must fully work with JS disabled. Everything here
   is enhancement — no frameworks, no dependencies, ~2KB gzipped.
   ========================================================================== */
(() => {
  'use strict';

  /* 1. Header: transparent over hero, solid after scroll ------------------ */
  const header = document.querySelector('.site-header');
  if (header && header.classList.contains('site-header--transparent')) {
    const onScroll = () =>
      header.classList.toggle('is-scrolled', window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* 2. Mobile navigation drawer ------------------------------------------ */
  const nav = document.getElementById('MobileNav');
  const openBtn = document.querySelector('[data-nav-open]');
  const closeBtn = document.querySelector('[data-nav-close]');
  if (nav && openBtn) {
    const setOpen = (open) => {
      nav.classList.toggle('is-open', open);
      openBtn.setAttribute('aria-expanded', open);
      document.body.style.overflow = open ? 'hidden' : '';
      (open ? closeBtn : openBtn).focus();
    };
    openBtn.addEventListener('click', () => setOpen(true));
    closeBtn && closeBtn.addEventListener('click', () => setOpen(false));
    nav.addEventListener('keydown', (e) => e.key === 'Escape' && setOpen(false));
  }

  /* 3. Scroll reveal (respects prefers-reduced-motion via CSS) ------------
     Editor-safe: Shopify's customizer re-renders section HTML without
     re-running this script, so (a) in design mode we never hide reveal
     content at all, and (b) we re-arm the observer on shopify:section:load
     so live-preview behavior stays correct for apps and future scripts.  */
  const inDesignMode = window.Shopify && Shopify.designMode;
  if (inDesignMode) document.documentElement.classList.add('design-mode');

  const revealIO = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            revealIO.unobserve(e.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px' })
    : null;

  const armReveals = (root = document) => {
    root.querySelectorAll('.reveal:not(.is-in)').forEach((el) => {
      if (revealIO && !inDesignMode) revealIO.observe(el);
      else el.classList.add('is-in');
    });
  };
  armReveals();
  document.addEventListener('shopify:section:load', (e) => armReveals(e.target));

  /* 4. Product: sticky mobile add-to-cart bar ----------------------------- */
  const stickyBar = document.querySelector('.sticky-atc');
  const buyBox = document.getElementById('BuyButtons');
  if (stickyBar && buyBox && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
    }).observe(buyBox);
    // Sticky button submits the real form — one source of truth.
    stickyBar.querySelector('button')?.addEventListener('click', () => {
      document.getElementById('ProductForm')?.requestSubmit();
    });
  }

  /* 5. Product: tap-to-zoom gallery --------------------------------------- */
  document.querySelectorAll('.gallery__item').forEach((item) => {
    item.addEventListener('click', (e) => {
      item.classList.toggle('is-zoomed');
      const img = item.querySelector('img');
      if (item.classList.contains('is-zoomed') && img) {
        const r = item.getBoundingClientRect();
        img.style.transformOrigin =
          `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
      }
    });
  });

  /* 6. Product: variant selection → price, availability, URL -------------- */
  const productForm = document.getElementById('ProductForm');
  const variantData = document.getElementById('ProductVariants');
  if (productForm && variantData) {
    const variants = JSON.parse(variantData.textContent);
    const money = (cents) =>
      new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: variantData.dataset.currency || 'USD',
      }).format(cents / 100);

    productForm.addEventListener('change', () => {
      const selected = [...productForm.querySelectorAll('input[type=radio]:checked')]
        .map((i) => i.value);
      const match = variants.find((v) =>
        v.options.every((o, i) => o === selected[i]));
      if (!match) return;

      productForm.querySelector('[name=id]').value = match.id;

      const priceEl = document.querySelector('[data-product-price]');
      if (priceEl) priceEl.textContent = money(match.price);

      const btn = productForm.querySelector('[data-add-to-cart]');
      if (btn) {
        btn.disabled = !match.available;
        btn.textContent = match.available
          ? btn.dataset.labelAdd
          : btn.dataset.labelSoldOut;
      }
      // Keep URL shareable per-variant without a page load.
      const url = new URL(window.location);
      url.searchParams.set('variant', match.id);
      history.replaceState({}, '', url);
    });
  }

  /* 7. Collection: sort select navigates ---------------------------------- */
  const sortSelect = document.getElementById('SortBy');
  sortSelect?.addEventListener('change', () => {
    const url = new URL(window.location);
    url.searchParams.set('sort_by', sortSelect.value);
    window.location.assign(url);
  });

  /* 8. Recently viewed (localStorage, product page writes / section reads) */
  try {
    const KEY = 'twf:recent';
    const holder = document.querySelector('[data-recently-viewed]');
    const current = document.querySelector('[data-product-handle]');
    let recent = JSON.parse(localStorage.getItem(KEY) || '[]');

    if (current) {
      const handle = current.dataset.productHandle;
      recent = [handle, ...recent.filter((h) => h !== handle)].slice(0, 8);
      localStorage.setItem(KEY, JSON.stringify(recent));
    }
    if (holder) {
      const handles = recent
        .filter((h) => h !== current?.dataset.productHandle)
        .slice(0, 4);
      if (!handles.length) {
        holder.closest('.section')?.remove();
      } else {
        Promise.all(
          handles.map((h) =>
            fetch(`/products/${h}.js`).then((r) => (r.ok ? r.json() : null)))
        ).then((products) => {
          holder.innerHTML = products.filter(Boolean).map((p) => `
            <a class="card" href="${p.url}">
              <div class="card__media">
                <img class="card__img--primary" src="${p.featured_image}&width=600" alt="" loading="lazy" width="600" height="750">
              </div>
              <div class="card__info">
                <h3 class="card__title">${p.title}</h3>
                <div class="card__price">${money(p.price)}</div>
              </div>
            </a>`).join('');
          function money(c) {
            return new Intl.NumberFormat('en', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(c / 100);
          }
        });
      }
    }
  } catch (_) { /* localStorage unavailable — feature silently absent */ }
})();